import {
  addSpeakersToSpeakerArray,
  parseWordsArray,
  parseClassifiersArray,
  b64toBlob,
} from "./utility";

const parseKeywords = (keywords) =>
  keywords
    .filter((k) => k.mentions)
    .map((k) => ({
      name: k.keyword,
      score: !isNaN(k.relevance) ? k.relevance : 1,
      t: k.mentions
        .map((m) => ({ [m.speakerName]: m.occurrences.map((o) => o.s) }))
        .reduce((acc, m) => Object.assign(acc, m), {}),
      internalName: [],
    }));
const parseUniqueSpeakersFromKeywords = (keywords) => [
  ...keywords
    .filter((k) => k.mentions)
    .map((k) => k.mentions.map((m) => m.speakerName))
    .reduce((acc, s) => acc.concat(s), [])
    .reduce((acc, s) => acc.add(s), new Set()),
];

const parse = (data) => {
  let classifiers = [];
  let detectors = [];
  let groups = [];
  let metrics = [];
  let metricGroups = [];
  let entities = [];
  let speakers = [];
  let topics = [];
  let keyWords = [];
  let transcript = [];
  let fullTurns = [];
  let transcriptSpeakers = [];
  let transcriptCC = "";
  let interactionType = "";
  const verbArray = [];
  let summary = [];

  if (data.spotting) {
    if (data.spotting.groups) {
      groups = groups.concat(
        data.spotting.groups.map((g) => ({
          name: g.groupName,
          keywords: parseKeywords(g.spottedKeywords),
          speakers: parseUniqueSpeakersFromKeywords(g.spottedKeywords),
          score: 1,
          similarCategories: [],
          subcategories: [],
          type: "category",
        }))
      );
    }
  }

  if (data.knowledge) {
    if (data.knowledge.topics) {
      topics = topics.concat(
        data.knowledge.topics.map((t) => ({
          name: t.topicName,
          keywords: parseKeywords(t.keywords),
          speakers: parseUniqueSpeakersFromKeywords(t.keywords),
          score: t.relevance,
          similarCategories: [],
          subcategories: [],
          type: "category",
        }))
      );
    }

    if (data.knowledge.keywords) {
      topics.push({
        name: "Unsorted",
        type: "unsorted",
        keywords: parseKeywords(data.knowledge.keywords),
        speakers: parseUniqueSpeakersFromKeywords(data.knowledge.keywords),
      });
    }
  }

  if (data.transcript) {
    if (data.transcript.words) {
      const {
        words: wordsArrayWords,
        speakers: wordsArraySpeakers,
        transcriptSpeakers: wordsArrayTranscriptSpeakers,
      } = parseWordsArray(data.transcript.words);
      transcript = transcript.concat(wordsArrayWords);
      speakers = addSpeakersToSpeakerArray(speakers, wordsArraySpeakers);
      transcriptSpeakers = wordsArrayTranscriptSpeakers;
      fullTurns = data?.transcript?.turns || data?.messages;
    }

    if (
      data.transcript.alternateFormats &&
      data.transcript.alternateFormats.length
    ) {
      // Only webvtt is supported, so we can ignore other formats.
      const webvttFormat = data.transcript.alternateFormats.find(
        (alternateFormat) => alternateFormat.format === "webvtt"
      );

      if (webvttFormat) {
        // Since HTML5 track element only supports inclusion through src, we have to
        // turn the vtt contents into a blob, then convert the blob into a DomString (think dataURI).
        transcriptCC = URL.createObjectURL(
          b64toBlob(webvttFormat.data, webvttFormat.contentType)
        );
      }
    }
  }

  if (data.interactionType) {
    interactionType = data.interactionType;
  }

  if (data.prediction && data.prediction.classifiers) {
    classifiers = parseClassifiersArray(data.prediction.classifiers);
  }

  if (data.summary) {
    summary = data.summary;
  }
  if (data.metrics) {
    metrics = data.metrics; // eslint-disable-line prefer-destructuring
    metrics.forEach((metricGroup) => {
      metricGroup.metricValues.forEach((metric) => {
        if (metric.metricName === "call-change-in-sentiment") {
          metric.metricName = "intra-call-change-in-sentiment";
        } else if (metric.metricName === "agent-to-caller-talk-rate-ratio") {
          metric.metricName = "intra-agent-to-caller-talk-rate-ratio";
        }
      });
    });
  }

  if (data.metricGroups) {
    metricGroups = data.metricGroups.map((metricGroup) => {
      return {
        ...metricGroup,
        metrics: metricGroup.metrics.map((metric) => {
          if (metric.metricName === "call-change-in-sentiment") {
            metric.metricName = "intra-call-change-in-sentiment";
          } else if (metric.metricName === "agent-to-caller-talk-rate-ratio") {
            metric.metricName = "intra-agent-to-caller-talk-rate-ratio";
          }
          return metric;
        }),
      };
    });
  }

  if (data.categories) {
    const catArray = [];

    data.categories
      .filter((category) => category.categoryValue)
      .forEach((category, index) => {
        const occurrence = {};
        const keywords = [];
        const speakers = [];

        (category.categoryMatches || []).forEach((val, indx) => {
          const speaker = val.speakerName;
          speakers.push(speaker);
          val.occurrences.forEach((v, i) => {
            const keyName = v.exact.split(" ").join("_");
            if (keyName in occurrence) {
              occurrence[keyName].start.push(v.s);
              occurrence[keyName].end.push(v.e);
              occurrence[keyName].hits.push({ s: v.s, w: speaker });
            } else {
              const tempStart = [];
              const tempEnd = [];
              const tempObj = {};
              const hits = [];
              const tempHit = {};
              tempStart.push(v.s);
              tempEnd.push(v.e);
              tempHit.s = v.s;
              tempHit.w = speaker;
              hits.push(tempHit);

              tempObj.start = tempStart;
              tempObj.end = tempEnd;
              tempObj.exact = v.exact;
              tempObj.hits = hits;
              occurrence[keyName] = tempObj;
              keywords.push(v.exact);
            }
          });
        });
        catArray.push({
          name: category.categoryName,
          type: "category",
          occurrences: Object.values(occurrence),
          keywords,
          speakers,
        });
      });
    catArray.forEach((value) => {
      const keywords = [];
      value.occurrences.forEach((val) => {
        const speaker = value.speakers[0];
        keywords.push({
          name: val.exact,
          score: 1,
          internalName: [],
          t: { speaker: val.start },
        });
      });
      value.keywords = keywords;
    });
    data.categories = catArray;
  };

  //llm categories
    if (data.llmCategories) {
    const llmcatArray = [];
    data.llmCategories
      .forEach((category, index) => {
        llmcatArray.push({
          name: category.categoryName,
          type: "llmCategory",
          occurrences: [],
          keywords : [],
          speakers: [],
          categoryValue: category.categoryValue,
          onClickEnabled: false,
        });
      });
    data.llmCategories = llmcatArray;
  }
  //end llm categories

  if (data.conversation) {
    data.conversation.speakerVerbNounPairs.forEach((category, index) => {
      const occurrence = {};
      const keywords = [];
      const speakers = [category?.speakerName];

      (category?.verbNounPairs || []).forEach((val, indx) => {
        const tempStart = [];
        const tempEnd = [];
        const tempObj = {};
        const hits = [];
        const tempHit = {};
        tempStart.push(val.s);
        tempEnd.push(val.e);
        tempHit.s = val.s;
        tempHit.w = category?.speakerName;
        hits.push(tempHit);
        tempObj.start = tempStart;
        tempObj.end = tempEnd;
        tempObj.hits = hits;
        verbArray.push({
          name: val?.verb + " " + val?.noun,
          type: "category",
          occurrences: [tempObj],
          keywords,
          speakers,
        });
      });
    });
  }

  if (data.prediction && data.prediction.detectors) {
    detectors = data.prediction.detectors.reduce(
      (acc, detector) =>
        detector.detections
          .reduce(
            (acc2, detection) =>
              detection.detectedSegments
                .reduce(
                  (acc3, s) =>
                    acc3.concat({
                      name: detector.detectorName,
                      detector: detector.detectorId,
                      class: detection.detectorClass,
                      title: detection.detectorClassLabel,
                      channel: null,
                      intervals: s.occurrences.map(({ s: start, e: end }) => ({
                        start,
                        end,
                      })),
                    }),
                  []
                )
                .concat(acc2),
            []
          )
          .concat(acc),
      []
    );
  }

  if (data.entities) {
    let finalEntityGroup = [];
    let entityGroup = {};
    data.entities.forEach((entity) => {
      const { type, ...rest } = entity;
      if (entityGroup[type]) {
        entityGroup[entity.type].push(rest);
      } else {
        entityGroup[entity.type] = [rest];
      }
    });

    Object.keys(entityGroup).forEach((key) => {
      const texts = entityGroup[key];
      const groups = {};
      texts.forEach(({ text, ...rest }) => {
        if (groups[text]) {
          groups[text].push(rest);
        } else {
          groups[text] = [rest];
        }
      });

      const occurrences = Object.keys(groups).map((key) => {
        return {
          text: key,
          occurrences: groups[key],
        };
      });

      finalEntityGroup.push({
        name: key,
        keywords: occurrences,
        type: "category",
      });
    });

    entities = finalEntityGroup;
  }

  return {
    mediaContentType: data.mediaContentType,
    mediaId: data.mediaId,
    activeTopic: 0,
    topics,
    activeGroup: 0,
    groups,
    activeSpeaker: 0,
    speakers,
    transcriptSpeakers,
    transcript,
    fullTurns,
    interactionType,
    transcriptCC,
    classifiers: classifiers || [],
    verbs: verbArray || [],
    metrics: metrics || [],
    metricGroups: metricGroups,
    categories: data.categories || [],
    detectors: detectors || [],
    speakerSentiments: data.speakerSentiments || [],
    metadata: { contentType: data.mediaContentType, detail: data.metadata },
    entities,
    summary,
    llmCategories: data.llmCategories || [],
  };
};

export default parse;
